import { supabase } from './supabase';

interface UserProfileLookup {
  user_id: string;
  full_name: string | null;
  company_name?: string | null;
  email?: string | null;
}

export interface SellerReview {
  id: string;
  seller_id?: string | null;
  reviewer_id?: string | null;
  display_seller_name?: string | null;
  display_reviewer_name?: string | null;
  display_date?: string | null;
  reliability_rating: number;
  response_speed_rating: number;
  engineer_efficiency_rating: number;
  honesty_rating: number;
  overall_rating: number;
  comment?: string;
  order_id?: string;
  service_request_id?: string;
  created_at: string;
  updated_at?: string;
  reviewer?: {
    full_name: string | null;
    email?: string | null;
  };
  seller?: {
    full_name: string | null;
    company_name: string | null;
  };
}

export interface SellerAverageRatings {
  reliability_avg: number;
  response_speed_avg: number;
  engineer_efficiency_avg: number;
  honesty_avg: number;
  overall_avg: number;
  total_reviews: number;
}

const buildProfileMap = (profiles: UserProfileLookup[] | null | undefined) =>
  new Map((profiles ?? []).map((profile) => [profile.user_id, profile]));

const attachSellerProfiles = (
  reviews: Omit<SellerReview, 'seller'>[],
  profiles: UserProfileLookup[] | null | undefined
): SellerReview[] => {
  const profileMap = buildProfileMap(profiles);

  return reviews.map((review) => {
    // Prefer manual name override (admin-created fake review)
    if (review.display_seller_name) {
      return {
        ...review,
        seller: { full_name: review.display_seller_name, company_name: null },
      };
    }
    const sellerProfile = review.seller_id ? profileMap.get(review.seller_id) : undefined;
    return {
      ...review,
      seller: sellerProfile
        ? { full_name: sellerProfile.full_name, company_name: sellerProfile.company_name ?? null }
        : undefined,
    };
  });
};

const attachReviewerProfiles = (
  reviews: SellerReview[],
  profiles: UserProfileLookup[] | null | undefined
): SellerReview[] => {
  const profileMap = buildProfileMap(profiles);

  return reviews.map((review) => {
    // Prefer manual name override (admin-created fake review)
    if (review.display_reviewer_name) {
      return { ...review, reviewer: { full_name: review.display_reviewer_name } };
    }
    if (!review.reviewer_id) {
      return review;
    }
    const reviewerProfile = profileMap.get(review.reviewer_id);
    return {
      ...review,
      reviewer: reviewerProfile
        ? { full_name: reviewerProfile.full_name, email: reviewerProfile.email ?? null }
        : undefined,
    };
  });
};

export const sellerReviewService = {
  // Get all reviews for public display
  async getAllReviews(page: number = 1, limit: number = 9): Promise<{ data: SellerReview[] | null; count: number; error: any }> {
    try {
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      const { data: reviews, error: reviewsError, count } = await supabase
        .from('seller_reviews')
        .select(`
          id,
          seller_id,
          reviewer_id,
          display_seller_name,
          display_reviewer_name,
          display_date,
          reliability_rating,
          response_speed_rating,
          engineer_efficiency_rating,
          honesty_rating,
          overall_rating,
          comment,
          created_at
        `, { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (reviewsError) {
        throw reviewsError;
      }

      const sellerIds = Array.from(
        new Set((reviews ?? []).map((review) => review.seller_id).filter(Boolean))
      );

      if (sellerIds.length === 0) {
        return { data: (reviews ?? []) as SellerReview[], count: count || 0, error: null };
      }

      const { data: sellerProfiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, company_name')
        .in('user_id', sellerIds);

      if (profilesError) {
        throw profilesError;
      }

      return {
        data: attachSellerProfiles((reviews ?? []) as Omit<SellerReview, 'seller'>[], sellerProfiles),
        count: count || 0,
        error: null,
      };
    } catch (error) {
      console.error('Error fetching all reviews:', error);
      return { data: null, count: 0, error };
    }
  },

  // Get reviews for a specific seller
  async getSellerReviews(sellerId: string): Promise<{ data: SellerReview[] | null; error: any }> {
    try {
      const { data: reviews, error: reviewsError } = await supabase
        .from('seller_reviews')
        .select('*')
        .eq('seller_id', sellerId)
        .order('created_at', { ascending: false });

      if (reviewsError) {
        throw reviewsError;
      }

      const reviewerIds = Array.from(
        new Set((reviews ?? []).map((review) => review.reviewer_id).filter(Boolean))
      ) as string[];

      if (reviewerIds.length === 0) {
        return { data: (reviews ?? []) as SellerReview[], error: null };
      }

      const { data: reviewerProfiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, full_name')
        .in('user_id', reviewerIds);

      if (profilesError) {
        throw profilesError;
      }

      return {
        data: attachReviewerProfiles((reviews ?? []) as SellerReview[], reviewerProfiles),
        error: null,
      };
    } catch (error) {
      console.error('Error fetching seller reviews:', error);
      return { data: null, error };
    }
  },

  // Get average ratings for a seller using the RPC function
  async getSellerAverageRatings(sellerId: string): Promise<{ data: SellerAverageRatings | null; error: any }> {
    try {
      const { data, error } = await supabase.rpc('get_seller_average_ratings', {
        seller_uuid: sellerId
      });

      if (error) throw error;
      return { data: data[0] as SellerAverageRatings, error: null };
    } catch (error) {
      console.error('Error fetching seller average ratings:', error);
      return { data: null, error };
    }
  },

  // Update a review (Admin only) — overall_rating is a GENERATED column, excluded automatically
  async updateReview(reviewId: string, updates: Partial<Omit<SellerReview, 'id' | 'overall_rating' | 'created_at' | 'updated_at' | 'seller' | 'reviewer'>>): Promise<{ data: SellerReview | null; error: any }> {
    try {
      // Strip out overall_rating in case it slips through from the UI
      const { overall_rating: _, ...safeUpdates } = updates as any;
      const { data, error } = await supabase
        .from('seller_reviews')
        .update(safeUpdates)
        .eq('id', reviewId)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating review:', error);
      return { data: null, error };
    }
  },

  // Create a manual review (Admin only)
  // overall_rating is a GENERATED column — never include it in the insert
  async createReview(review: {
    display_seller_name: string;
    display_reviewer_name: string;
    reliability_rating: number;
    response_speed_rating: number;
    engineer_efficiency_rating: number;
    honesty_rating: number;
    comment?: string;
    created_at?: string;
  }): Promise<{ data: SellerReview | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('seller_reviews')
        .insert([review])
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating review:', error);
      return { data: null, error };
    }
  },

  // Delete a review (Admin only)
  async deleteReview(reviewId: string): Promise<{ error: any }> {
    try {
      const { error } = await supabase
        .from('seller_reviews')
        .delete()
        .eq('id', reviewId);

      return { error };
    } catch (error) {
      console.error('Error deleting review:', error);
      return { error };
    }
  }
};